import { useEffect, useState } from "react";
import { Download, ExternalLink, Image as ImageIcon, Loader2 } from "lucide-react";
import { PageHero } from "@/components/layout/PageHero";
import { api, BACKEND } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { translateError } from "@/i18n/errors";

// Signed, cookie-less links opened in a new top-level tab: downloads work in iframes, PWA and every mobile browser.
const fileUrl = (item, part, download) => `${BACKEND}${item.links[part]}${download ? "?download=1" : ""}`;

const PromoCard = ({ item }) => {
  const { t, fmtBytes } = useI18n();
  const k = item.kind.toLowerCase();
  return (
    <article className="card-carbon flex min-w-0 flex-col gap-4 rounded-2xl p-4 sm:p-5" data-testid={`promo-card-${k}`}>
      <div className="flex flex-col items-start gap-2">
        <h2 className="font-display text-base font-bold uppercase tracking-wider text-[var(--gold)] md:text-lg" data-testid={`promo-title-${k}`}>{t(`promo.kind${item.kind}`)}</h2>
        <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${item.draft ? "bg-red-600/20 text-red-300" : "bg-emerald-500/15 text-emerald-300"}`} data-testid={`promo-status-${k}`}>
          {item.draft ? t("promo.draft") : t("promo.final")}
        </span>
      </div>
      <video
        className="mx-auto aspect-[9/16] w-full max-w-[340px] rounded-xl border border-neutral-800 bg-black object-cover"
        src={fileUrl(item, "video")}
        poster={fileUrl(item, "cover")}
        controls
        playsInline
        preload="metadata"
        data-testid={`promo-player-${k}`}
      />
      <dl className="grid grid-cols-2 gap-2 text-xs text-neutral-400">
        <div><dt>{t("promo.duration")}</dt><dd className="font-semibold text-neutral-100" data-testid={`promo-duration-${k}`}><bdi>{item.duration.toFixed(1)} s</bdi></dd></div>
        <div><dt>{t("promo.size")}</dt><dd className="font-semibold text-neutral-100" data-testid={`promo-size-${k}`}><bdi>{fmtBytes(item.size)}</bdi></dd></div>
        <div className="col-span-2"><dd className="font-mono text-[11px] text-neutral-500" data-testid={`promo-specs-${k}`}><bdi>{item.width}×{item.height} · {item.vcodec.toUpperCase()} + {(item.acodec || "").toUpperCase()} · {item.domain.toUpperCase()}</bdi></dd></div>
      </dl>
      <div className="flex flex-col gap-2 sm:flex-row">
        <a className="btn-gold btn-sm flex-1 justify-center" href={fileUrl(item, "video", true)} target="_blank" rel="noopener noreferrer" data-testid={`promo-download-video-${k}`}>
          <Download className="h-4 w-4" /> {t("promo.download")}
        </a>
        <a className="btn-red btn-sm flex-1 justify-center" href={fileUrl(item, "cover", true)} target="_blank" rel="noopener noreferrer" data-testid={`promo-download-cover-${k}`}>
          <ImageIcon className="h-4 w-4" /> {t("promo.coverDownload")} <bdi className="text-neutral-500">({fmtBytes(item.cover_size)})</bdi>
        </a>
      </div>
      <p className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <a className="inline-flex items-center gap-1 text-[var(--gold-bright)] underline-offset-2 hover:underline" href={fileUrl(item, "video")} target="_blank" rel="noopener noreferrer" data-testid={`promo-open-video-${k}`}>
          <ExternalLink className="h-3.5 w-3.5" /> {t("promo.open")}
        </a>
        <a className="inline-flex items-center gap-1 text-neutral-300 underline-offset-2 hover:underline" href={fileUrl(item, "cover")} target="_blank" rel="noopener noreferrer" data-testid={`promo-open-cover-${k}`}>
          <ExternalLink className="h-3.5 w-3.5" /> {t("promo.openCover")}
        </a>
      </p>
      <p className="text-[11px] text-neutral-500" data-testid={`promo-save-hint-${k}`}>{t("promo.saveHint")}</p>
      <PublicControls item={item} k={k} />
    </article>
  );
};

const PublicControls = ({ item, k }) => {
  const { t } = useI18n();
  const [pub, setPub] = useState(item.public !== false);
  const [order, setOrder] = useState(String(item.order ?? 0));
  const save = (body) => api.patch(`/admin/promo/${item.kind}`, body).then(() => toast.success(t("videos.saved"))).catch((err) => toast.error(translateError(err, t)));
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-3 border-t border-neutral-800 pt-3 text-xs text-neutral-300" data-testid={`promo-public-${k}`}>
      <label className="flex items-center gap-2"><Switch checked={pub} onCheckedChange={(v) => { setPub(v); save({ public: v }); }} data-testid={`promo-public-toggle-${k}`} /> {t("videos.publicToggle")}</label>
      <label className="flex items-center gap-2">{t("videos.order")}
        <Input inputMode="numeric" value={order} onChange={(e) => setOrder(e.target.value)} onBlur={() => save({ order: Math.max(0, Math.min(99, parseInt(order, 10) || 0)) })}
          className="h-8 w-16 bg-black/50" data-testid={`promo-order-${k}`} /></label>
      <a href={`/videos#video-${k}`} target="_blank" rel="noopener noreferrer" className="text-[var(--gold-bright)] underline-offset-2 hover:underline" data-testid={`promo-public-link-${k}`}>/videos</a>
    </div>
  );
};

export default function AdminPromo() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/admin/promo").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, []);
  return (
    <div data-testid="admin-promo-page">
      <PageHero eyebrow={t("admin.eyebrow")} title={t("promo.title")}>
        <p className="mt-4 max-w-2xl text-sm text-neutral-400">{t("promo.intro")}</p>
      </PageHero>
      <div className="mx-auto max-w-6xl px-4 pb-16 sm:px-8">
        {!items && <Loader2 className="h-6 w-6 animate-spin text-[var(--gold)]" data-testid="promo-loading" />}
        {items && !items.length && <p className="card-carbon rounded-2xl p-5 text-sm text-neutral-400" data-testid="promo-empty">{t("promo.empty")}</p>}
        {items && items.length > 0 && (
          <div className="grid gap-6 md:grid-cols-2" data-testid="promo-list">
            {items.map((it) => <PromoCard key={it.kind} item={it} />)}
          </div>
        )}
      </div>
    </div>
  );
}
